import React, { useState } from 'react';
import Header from '../../components/Header/Header';
import { TechnicalAlert, SIGNAL_TYPES, SIGNAL_EXPLANATIONS, SEVERITY_COLORS, SEVERITY_LABELS } from './TechnicalAlertsTypes';

interface DesktopTechnicalAlertsProps {
  filteredAlerts: TechnicalAlert[];
  disabledSignals: Set<string>;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
  filterType: string;
  setFilterType: (type: string) => void;
  filterSeverity: string;
  setFilterSeverity: (sev: string) => void;
  filterSymbol: string;
  setFilterSymbol: (sym: string) => void;
  filterDays: number;
  setFilterDays: (days: number) => void;
  stocks: { symbole: string; nom_societe?: string }[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  totalAlerts: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  loading: boolean;
  error: string;
  groupedByDate: Record<string, TechnicalAlert[]>;
  toggleSignal: (key: string) => void;
  enableAll: () => void;
  formatDate: (date: string) => string;
  getSignalInfo: (type: string) => any;
}

const DesktopTechnicalAlerts: React.FC<DesktopTechnicalAlertsProps> = ({
  filteredAlerts,
  disabledSignals,
  showSettings,
  setShowSettings,
  filterType,
  setFilterType,
  filterSeverity,
  setFilterSeverity,
  filterSymbol,
  setFilterSymbol,
  filterDays,
  setFilterDays,
  stocks,
  currentPage,
  setCurrentPage,
  totalPages,
  totalAlerts,
  pageSize,
  setPageSize,
  loading,
  error,
  groupedByDate,
  toggleSignal,
  enableAll,
  formatDate,
  getSignalInfo,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const toggleExplanation = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <>
      <Header icon='notifications_active' title='Signaux Pro' />
      <div style={{ padding: '0 16px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Top bar: stats + settings toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 900, color: 'var(--text-heading)' }}>{filteredAlerts.length}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Signaux</div>
            </div>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 900, color: '#ef4444' }}>
                {filteredAlerts.filter(a => a.severity === 'HIGH').length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Priorité Haute</div>
            </div>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 900, color: '#16a34a' }}>
                {SIGNAL_TYPES.length - disabledSignals.size}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Types Actifs</div>
            </div>
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
              border: showSettings ? '2px solid #2563eb' : '1px solid var(--border-color)',
              background: showSettings ? 'rgba(37, 99, 235, 0.08)' : 'var(--card-bg)',
              color: 'var(--text-heading)', fontWeight: 600, fontSize: '14px'
            }}
          >
            <i className="material-icons" style={{ fontSize: '18px' }}>tune</i>
            Configurer mes alertes
          </button>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '20px', marginBottom: '20px',
            animation: 'fadeIn 0.2s ease'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: 'var(--text-heading)', fontSize: '16px' }}>
                <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>tune</i>
                Types d'alertes à recevoir
              </h3>
              <button onClick={enableAll} style={{
                background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600, padding: '4px 8px'
              }}>
                Tout activer
              </button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '0 0 16px' }}>
              Désactivez les types de signaux qui ne vous intéressent pas. Les alertes désactivées ne seront plus affichées.
            </p>

            {/* Group by category */}
            {['Tendance', 'Oscillateur', 'Volume', 'Niveaux', 'Volatilité', 'Valorisation', 'Marché', 'Comportemental'].map(cat => {
              const signals = SIGNAL_TYPES.filter(s => s.category === cat);
              return (
                <div key={cat} style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>
                    {cat}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {signals.map(sig => {
                      const isEnabled = !disabledSignals.has(sig.key);
                      return (
                        <button
                          key={sig.key}
                          onClick={() => toggleSignal(sig.key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', borderRadius: '20px', fontSize: '13px',
                            cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s',
                            border: isEnabled ? '1px solid #2563eb' : '1px solid var(--border-color)',
                            background: isEnabled ? 'rgba(37, 99, 235, 0.08)' : 'var(--bg-secondary)',
                            color: isEnabled ? '#2563eb' : 'var(--text-muted)',
                            opacity: isEnabled ? 1 : 0.6,
                          }}
                        >
                          <span>{sig.emoji}</span>
                          <span>{sig.label}</span>
                          <i className="material-icons" style={{ fontSize: '16px' }}>
                            {isEnabled ? 'check_circle' : 'radio_button_unchecked'}
                          </i>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Stock Filter */}
          <select
            value={filterSymbol}
            onChange={e => setFilterSymbol(e.target.value)}
            style={{
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
              border: '1px solid var(--border-color)', background: 'var(--card-bg)',
              color: 'var(--text-heading)', cursor: 'pointer', fontWeight: 600,
            }}
          >
            <option value="ALL">🏢 Toutes les sociétés ({stocks.length || 48})</option>
            {stocks.map(s => (
              <option key={s.symbole} value={s.symbole}>
                {s.symbole} {s.nom_societe ? `- ${s.nom_societe}` : ''}
              </option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            style={{
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
              border: '1px solid var(--border-color)', background: 'var(--card-bg)',
              color: 'var(--text-heading)', cursor: 'pointer'
            }}
          >
            <option value="ALL">Tous les types</option>
            {SIGNAL_TYPES.filter(s => !disabledSignals.has(s.key)).map(s => (
              <option key={s.key} value={s.key}>{s.emoji} {s.label}</option>
            ))}
          </select>

          {/* Severity Filter */}
          <select
            value={filterSeverity}
            onChange={e => setFilterSeverity(e.target.value)}
            style={{
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
              border: '1px solid var(--border-color)', background: 'var(--card-bg)',
              color: 'var(--text-heading)', cursor: 'pointer'
            }}
          >
            <option value="ALL">Toutes priorités</option>
            <option value="HIGH">🔴 Haute</option>
            <option value="MEDIUM">🟡 Moyenne</option>
            <option value="LOW">⚪ Faible</option>
          </select>

          {/* History Horizon Filter */}
          <select
            value={filterDays}
            onChange={e => setFilterDays(Number(e.target.value))}
            style={{
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
              border: '1px solid var(--border-color)', background: 'var(--card-bg)',
              color: 'var(--text-heading)', cursor: 'pointer'
            }}
          >
            <option value={7}>📅 7 derniers jours</option>
            <option value={14}>📅 14 derniers jours</option>
            <option value={30}>📅 1 mois d'historique (30j)</option>
            <option value={60}>📅 2 mois d'historique (60j)</option>
            <option value={90}>📅 3 mois d'historique (90j)</option>
          </select>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            <i className="material-icons" style={{ fontSize: '36px', animation: 'spin 1s linear infinite' }}>sync</i>
            <p>Chargement des alertes...</p>
          </div>
        )}
        {error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
            <i className="material-icons" style={{ fontSize: '36px' }}>error_outline</i>
            <p>{error}</p>
          </div>
        )}

        {/* Alerts list grouped by date */}
        {!loading && !error && Object.keys(groupedByDate).length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <i className="material-icons" style={{ fontSize: '48px' }}>notifications_none</i>
            <p style={{ marginTop: '12px' }}>Aucune alerte technique pour le moment.</p>
            <p style={{ fontSize: '13px' }}>Les signaux sont détectés quotidiennement après la clôture du marché.</p>
          </div>
        )}

        {!loading && !error && Object.entries(groupedByDate).map(([dateKey, dateAlerts]) => (
          <div key={dateKey} style={{ marginBottom: '28px' }}>
            <h3 style={{ color: 'var(--text-heading)', fontSize: '15px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="material-icons" style={{ fontSize: '18px', color: 'var(--text-muted)' }}>calendar_today</i>
              {formatDate(dateKey)}
              <span style={{
                background: 'var(--bg-secondary)', padding: '2px 10px', borderRadius: '12px',
                fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)'
              }}>
                {dateAlerts.length} signaux
              </span>
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {dateAlerts.map(alert => {
                const info = getSignalInfo(alert.signal_type);
                const isExpanded = expandedIds.has(alert.id);
                const explanation = SIGNAL_EXPLANATIONS[alert.signal_type];
                return (
                  <div
                    key={alert.id}
                    style={{
                      background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                      borderRadius: '10px', padding: '14px 18px',
                      borderLeft: `4px solid ${SEVERITY_COLORS[alert.severity] || '#6b7280'}`,
                      display: 'flex', alignItems: 'flex-start', gap: '14px',
                      transition: 'box-shadow 0.15s',
                      cursor: 'pointer',
                    }}
                    onClick={() => window.location.href = `/data?symbol=${alert.symbole}`}
                  >
                    <div style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>
                      {info.emoji}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 700, color: 'var(--text-heading)', fontSize: '15px' }}>
                          {alert.symbole}
                        </span>
                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                          {info.label}
                        </span>
                        <span style={{
                          fontSize: '11px', fontWeight: 600, padding: '1px 8px',
                          borderRadius: '10px', color: '#fff',
                          background: SEVERITY_COLORS[alert.severity] || '#6b7280'
                        }}>
                          {SEVERITY_LABELS[alert.severity] || alert.severity}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {alert.signal_detail}
                      </p>
                      {alert.price_at_signal && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          Cours : <strong>{alert.price_at_signal.toLocaleString('fr-FR')} FCFA</strong>
                        </div>
                      )}
                      {isExpanded && explanation && (
                        <div style={{
                          marginTop: '10px', padding: '10px 12px',
                          background: 'var(--bg-secondary)', borderRadius: '8px',
                          fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6,
                          borderLeft: '3px solid #2563eb',
                        }}>
                          {explanation}
                        </div>
                      )}
                    </div>
                    {explanation && (
                      <button
                        onClick={(e) => toggleExplanation(alert.id, e)}
                        title={isExpanded ? 'Masquer l\'explication' : 'Comprendre ce signal'}
                        style={{
                          flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                          color: isExpanded ? '#2563eb' : 'var(--text-muted)',
                          padding: '4px', borderRadius: '50%',
                          display: 'flex', alignItems: 'center',
                        }}
                      >
                        <i className="material-icons" style={{ fontSize: '18px' }}>info_outline</i>
                      </button>
                    )}
                    <i className="material-icons" style={{ fontSize: '18px', color: 'var(--text-muted)', flexShrink: 0 }}>
                      chevron_right
                    </i>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Pagination Controls */}
        {!loading && !error && totalAlerts > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: '16px', padding: '16px 20px',
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', marginTop: '20px', marginBottom: '40px'
          }}>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Affichage de <strong style={{ color: 'var(--text-heading)' }}>{Math.min((currentPage - 1) * pageSize + 1, totalAlerts)}</strong> à <strong style={{ color: 'var(--text-heading)' }}>{Math.min(currentPage * pageSize, totalAlerts)}</strong> sur <strong style={{ color: 'var(--text-heading)' }}>{totalAlerts}</strong> signaux
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                style={{
                  padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                  border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                  color: currentPage <= 1 ? 'var(--text-muted)' : 'var(--text-heading)',
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage <= 1 ? 0.5 : 1
                }}
              >
                ← Précédent
              </button>

              <span style={{ fontSize: '13px', fontWeight: 600, padding: '0 8px', color: 'var(--text-heading)' }}>
                Page {currentPage} / {totalPages}
              </span>

              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                style={{
                  padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                  border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                  color: currentPage >= totalPages ? 'var(--text-muted)' : 'var(--text-heading)',
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= totalPages ? 0.5 : 1
                }}
              >
                Suivant →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DesktopTechnicalAlerts;
